import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { MapPin, Phone, Building } from "lucide-react";

const Locations = () => {
  const majorCities = [
    {
      name: "Toronto",
      region: "Greater Toronto Area",
      population: "2.9M",
      description: "Ontario's largest city and economic center, home to major commercial developments and residential projects.",
      services: ["Downtown Office Construction", "Condo Renovations", "Commercial Build-outs", "Residential Custom Homes"],
      slug: "toronto"
    },
    {
      name: "Ottawa",
      region: "National Capital Region", 
      population: "1.0M",
      description: "Canada's capital city with significant government and commercial construction opportunities.",
      services: ["Government Facility Construction", "Office Buildings", "Residential Development", "Heritage Renovations"],
      slug: "ottawa"
    },
    {
      name: "Mississauga",
      region: "Peel Region",
      population: "720K", 
      description: "Major suburban center with diverse residential and commercial construction needs.",
      services: ["Suburban Development", "Commercial Centers", "Industrial Construction", "Multi-Family Housing"],
      slug: "mississauga"
    },
    {
      name: "Hamilton",
      region: "Golden Horseshoe",
      population: "540K",
      description: "Historic steel city experiencing significant urban renewal and development.",
      services: ["Urban Revitalization", "Industrial Renovation", "Residential Construction", "Commercial Development"],
      slug: "hamilton"
    },
    {
      name: "London",
      region: "Southwestern Ontario",
      population: "420K",
      description: "Regional center with growing healthcare, education, and technology sectors.",
      services: ["Healthcare Facility Construction", "Educational Buildings", "Residential Projects", "Commercial Spaces"],
      slug: "london"
    },
    {
      name: "Kitchener-Waterloo",
      region: "Waterloo Region",
      population: "570K",
      description: "Technology hub with innovative construction projects and rapid growth.",
      services: ["Tech Office Construction", "Innovation Centers", "Mixed-Use Development", "Residential Growth"],
      slug: "kitchenerwaterloo"
    }
  ];

  const allOntarioCities = [
    {
      city: "St. Catharines",
      region: "Niagara Region",
      population: "141K",
      slug: "st-catharines-construction-services",
      summary: "Known as the Garden City, St. Catharines is growing with residential, commercial, and industrial construction opportunities.",
      link_text: "St. Catharines Services",
      detail_page: {
        title: "General Contracting & Construction in St. Catharines",
        intro: "St. Catharines, Ontario's Garden City, is home to rapid residential and commercial development. Blue Knight Contracting delivers trusted general contracting and construction services for homeowners, businesses, and industries across the Niagara Region.",
        cta: {
          button_text: "Get St. Catharines Quote",
          phone: "(647) 760-8854",
          licensed: "Licensed & Insured"
        },
        hero_image: "/images/st-catharines-construction.jpg",
        alt_text: "Downtown St. Catharines construction site with new developments",
        service_overview: "From residential builds to industrial renovations, Blue Knight Contracting provides complete construction services throughout St. Catharines and the Niagara Region.",
        featured_services: [
          {
            name: "Residential Developments",
            description: "Custom homes, subdivisions, and residential renovations across St. Catharines.",
            sub_services: [
              "Custom home builds",
              "Major renovations",
              "Heritage home restoration",
              "Basement finishing",
              "Home additions"
            ]
          },
          {
            name: "Commercial Construction",
            description: "Retail, office, and mixed-use development in St. Catharines.",
            sub_services: [
              "Retail store construction",
              "Office fit-outs",
              "Restaurant build-outs",
              "Warehouse conversions",
              "Shopping centers"
            ]
          },
          {
            name: "Industrial Renovations",
            description: "Upgrades and construction for St. Catharines' industrial sector.",
            sub_services: [
              "Manufacturing facilities",
              "Industrial warehouses",
              "Retrofits & expansions"
            ]
          },
          {
            name: "Downtown Revitalization",
            description: "Supporting urban growth and modernization projects in St. Catharines' downtown core.",
            sub_services: [
              "Office towers",
              "Mixed-use spaces",
              "Public infrastructure improvements"
            ]
          }
        ],
        specialties: [
          "Residential Construction",
          "Commercial Development",
          "Industrial Renovations",
          "Downtown Revitalization"
        ],
        projects: [
          {
            name: "Downtown Office Project",
            location: "St. Paul Street, St. Catharines",
            image: "/images/st-catharines-office.jpg",
            alt_text: "Modern office building under construction in downtown St. Catharines"
          },
          {
            name: "Residential Community Build",
            location: "North St. Catharines",
            image: "/images/st-catharines-housing.jpg",
            alt_text: "New subdivision development in St. Catharines"
          }
        ],
        faq: [
          {
            question: "Do you handle residential construction in St. Catharines?",
            answer: "Yes, Blue Knight Contracting provides full-service residential construction including custom homes, renovations, and additions across St. Catharines."
          },
          {
            question: "Do you build commercial properties in St. Catharines?",
            answer: "Absolutely. We specialize in retail, office, and mixed-use commercial construction projects across the Niagara Region."
          },
          {
            question: "Do you have experience with St. Catharines permits and building codes?",
            answer: "Yes, our team has deep knowledge of St. Catharines building codes, permit applications, and municipal requirements."
          }
        ]
      }
    },
    {
      city: "Niagara Falls",
      region: "Niagara Region",
      population: "95K",
      slug: "niagara-falls-construction-services",
      summary: "Tourism-driven city with significant hospitality, commercial, and residential construction demand.",
      link_text: "Niagara Falls Services",
      detail_page: {
        title: "General Contracting & Construction in Niagara Falls",
        intro: "Niagara Falls, Ontario's tourism capital, requires specialized construction for hospitality, residential, and commercial projects. Blue Knight Contracting delivers trusted contracting across the region.",
        cta: {
          button_text: "Get Niagara Falls Quote",
          phone: "(647) 760-8854",
          licensed: "Licensed & Insured"
        },
        hero_image: "/images/niagara-falls-construction.jpg",
        alt_text: "Niagara Falls skyline with hotels under construction",
        service_overview: "We provide full construction solutions in Niagara Falls, including hospitality builds, condo developments, and retail spaces.",
        featured_services: [
          {
            name: "Hospitality Construction",
            description: "Hotels, resorts, and tourism-focused developments.",
            sub_services: [
              "Hotel construction",
              "Resort upgrades",
              "Entertainment venues"
            ]
          },
          {
            name: "Residential Development",
            description: "Condos, apartments, and suburban homes.",
            sub_services: [
              "Condo towers",
              "Townhomes",
              "Custom residential builds"
            ]
          }
        ],
        faq: [
          {
            question: "Do you specialize in hotel and tourism construction in Niagara Falls?",
            answer: "Yes, we have extensive experience in hotel builds, resorts, and other hospitality-focused projects in Niagara Falls."
          }
        ]
      }
    },
    {
      city: "Niagara-on-the-Lake",
      slug: "niagara-on-the-lake-construction-services",
      summary: "Historic town known for tourism, wineries, and heritage properties.",
      link_text: "Niagara-on-the-Lake Services"
    },
    {
      city: "Welland",
      slug: "welland-construction-services",
      summary: "Industrial hub in the Niagara Region with strong residential growth.",
      link_text: "Welland Services"
    },
    {
      city: "Fort Erie",
      slug: "fort-erie-construction-services",
      summary: "Border city with residential and commercial construction opportunities.",
      link_text: "Fort Erie Services"
    },
    {
      city: "Port Colborne",
      slug: "port-colborne-construction-services",
      summary: "Lakeside community with residential and commercial projects.",
      link_text: "Port Colborne Services"
    },
    {
      city: "Thorold",
      slug: "thorold-construction-services",
      summary: "Growing city with residential and commercial construction demand.",
      link_text: "Thorold Services"
    },
    {
      city: "Windsor",
      slug: "windsor-construction-services",
      summary: "Automotive and manufacturing hub with diverse construction projects.",
      link_text: "Windsor Services"
    },
    {
      city: "Kingston",
      slug: "kingston-construction-services",
      summary: "Historic waterfront city with residential and institutional construction opportunities.",
      link_text: "Kingston Services"
    },
    {
      city: "Barrie",
      slug: "barrie-construction-services",
      summary: "Rapidly growing city with strong residential and commercial development.",
      link_text: "Barrie Services"
    },
    {
      city: "Sudbury",
      slug: "sudbury-construction-services",
      summary: "Northern Ontario's mining hub with residential and industrial construction needs.",
      link_text: "Sudbury Services"
    },
    {
      city: "Thunder Bay",
      slug: "thunder-bay-construction-services",
      summary: "Northern Ontario hub with residential, commercial, and industrial construction projects.",
      link_text: "Thunder Bay Services"
    },
    {
      city: "Oshawa",
      slug: "oshawa-construction-services",
      summary: "Durham Region's growing city with residential and automotive-driven construction demand.",
      link_text: "Oshawa Services"
    },
    {
      city: "Cambridge",
      slug: "cambridge-construction-services",
      summary: "Part of the Waterloo Region with rapid residential and commercial growth.",
      link_text: "Cambridge Services"
    },
    {
      city: "Waterloo",
      slug: "waterloo-construction-services",
      summary: "Tech hub with strong demand for office, institutional, and residential builds.",
      link_text: "Waterloo Services"
    },
    {
      city: "Guelph",
      slug: "guelph-construction-services",
      summary: "Historic and growing city with residential and commercial construction needs.",
      link_text: "Guelph Services"
    },
    {
      city: "Brantford",
      slug: "brantford-construction-services",
      summary: "Southern Ontario city with manufacturing, residential, and commercial growth.",
      link_text: "Brantford Services"
    },
    {
      city: "Chatham-Kent",
      slug: "chatham-kent-construction-services",
      summary: "Agricultural and industrial hub with diverse construction projects.",
      link_text: "Chatham-Kent Services"
    },
    {
      city: "Sarnia",
      slug: "sarnia-construction-services",
      summary: "Petrochemical hub with industrial and residential projects.",
      link_text: "Sarnia Services"
    },
    {
      city: "North Bay",
      slug: "north-bay-construction-services",
      summary: "Gateway to Northern Ontario with residential and industrial growth.",
      link_text: "North Bay Services"
    },
    {
      city: "Belleville",
      slug: "belleville-construction-services",
      summary: "Bay of Quinte city with residential, retail, and industrial development.",
      link_text: "Belleville Services"
    },
    {
      city: "Cornwall",
      slug: "cornwall-construction-services",
      summary: "Eastern Ontario city with residential and commercial projects.",
      link_text: "Cornwall Services"
    },
    {
      city: "Timmins",
      slug: "timmins-construction-services",
      summary: "Mining and industrial hub with residential development opportunities.",
      link_text: "Timmins Services"
    },
    {
      city: "Woodstock",
      slug: "woodstock-construction-services",
      summary: "Manufacturing-driven city with residential and commercial construction growth.",
      link_text: "Woodstock Services"
    },
    {
      city: "Sault Ste. Marie",
      slug: "sault-ste-marie-construction-services",
      summary: "Northern Ontario city with residential, commercial, and industrial projects.",
      link_text: "Sault Ste. Marie Services"
    },
    {
      city: "Peterborough",
      slug: "peterborough-construction-services",
      summary: "Growing city with healthcare, residential, and institutional construction demand.",
      link_text: "Peterborough Services"
    },
    {
      city: "Brampton",
      slug: "brampton-construction-services",
      summary: "Major GTA city with diverse residential and commercial construction projects.",
      link_text: "Brampton Services"
    },
    {
      city: "Vaughan",
      slug: "vaughan-construction-services",
      summary: "Fast-growing GTA city with high-rise, retail, and residential developments.",
      link_text: "Vaughan Services"
    },
    {
      city: "Markham",
      slug: "markham-construction-services",
      summary: "Technology-driven GTA city with strong residential and commercial growth.",
      link_text: "Markham Services"
    },
    {
      city: "Richmond Hill",
      slug: "richmond-hill-construction-services",
      summary: "GTA suburb with residential and institutional construction demand.",
      link_text: "Richmond Hill Services"
    },
    {
      city: "Oakville",
      slug: "oakville-construction-services",
      summary: "Upscale GTA city with custom residential and commercial builds.",
      link_text: "Oakville Services"
    },
    {
      city: "Burlington",
      slug: "burlington-construction-services",
      summary: "Growing lakeside community with strong residential and retail development.",
      link_text: "Burlington Services"
    },
    {
      city: "Milton",
      slug: "milton-construction-services",
      summary: "One of Canada's fastest growing communities with housing and commercial projects.",
      link_text: "Milton Services"
    },
    {
      city: "Newmarket",
      slug: "newmarket-construction-services",
      summary: "Rapidly developing York Region city with diverse construction opportunities.",
      link_text: "Newmarket Services"
    },
    {
      city: "Ajax",
      slug: "ajax-construction-services",
      summary: "Durham Region community with residential and commercial growth.",
      link_text: "Ajax Services"
    },
    {
      city: "Pickering",
      slug: "pickering-construction-services",
      summary: "Durham Region city with residential, retail, and industrial developments.",
      link_text: "Pickering Services"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Ontario Construction Services - All Locations
          </h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Blue Knight Contracting proudly serves communities across Ontario with professional construction, 
            contracting, and skilled trades services from Windsor to Ottawa and everywhere in between.
          </p>
          <Button variant="hero" size="lg" className="bg-primary-foreground text-white hover:bg-primary-foreground/90">
            <Phone className="mr-2 h-5 w-5" />
            Find Services in Your Area
          </Button>
        </div>
      </section>

      {/* Major Cities */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Major Ontario Cities We Serve
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive construction services in Ontario's largest cities and metropolitan areas.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {majorCities.map((city) => (
              <Card key={city.name} className="border-2 hover:border-primary/20 transition-colors">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <MapPin className="h-6 w-6 text-primary" />
                        {city.name}
                      </CardTitle>
                      <CardDescription className="text-base mt-2">
                        {city.region} • Population: {city.population}
                      </CardDescription>
                    </div>
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <Building className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{city.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Featured Services in {city.name}:</h4>
                    <ul className="grid grid-cols-2 gap-2 text-sm">
                      {city.services.map((service) => (
                        <li key={service} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <Link to={`/${city.slug}`} className="flex-1">
                      <Button variant="cta" className="w-full">
                        {city.name} Services
                      </Button>
                    </Link>
                    <Button variant="outline">
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Service Areas */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Additional Ontario Service Areas
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We also provide construction services to communities throughout Ontario, 
              including smaller cities and rural areas.
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
            {allOntarioCities.map((location) => (
              <Card key={location.city} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6 pb-6">
                  <MapPin className="h-5 w-5 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold text-base mb-2">{location.city}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{location.summary}</p>
                  <Button variant="outline" size="sm" className="w-full">
                    {location.link_text}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <div className="bg-background p-8 rounded-lg max-w-4xl mx-auto border-2 border-primary/20">
              <h3 className="text-2xl font-bold mb-4">Don't See Your Community Listed?</h3>
              <p className="text-lg text-muted-foreground mb-6">
                Blue Knight Contracting serves communities throughout Ontario. If your location isn't listed above, 
                we likely still provide services in your area. Contact us to confirm service availability and 
                discuss your construction project needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="cta" size="lg">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (416) 555-BLUE
                </Button>
                <Link to="/contact">
                  <Button variant="outline" size="lg">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Regional Construction Expertise
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our deep understanding of regional regulations, climate considerations, 
              and local market conditions ensures successful projects across Ontario.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Southern Ontario</CardTitle>
                <CardDescription>
                  GTA, Golden Horseshoe, and Southwestern regions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• High-density urban construction</li>
                  <li>• Commercial and residential development</li>
                  <li>• Heritage building renovation</li>
                  <li>• Mixed-use developments</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Eastern Ontario</CardTitle>
                <CardDescription>
                  Ottawa Valley, Kingston area, and surrounding regions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Government facility construction</li>
                  <li>• Rural and suburban development</li>
                  <li>• Seasonal construction planning</li>
                  <li>• Heritage preservation projects</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Northern Ontario</CardTitle>
                <CardDescription>
                  Sudbury, North Bay, Thunder Bay, and northern communities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Climate-resistant construction</li>
                  <li>• Industrial and mining facilities</li>
                  <li>• Remote project logistics</li>
                  <li>• Specialized northern building techniques</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Local SEO Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ontario's Trusted Construction Partner
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="text-lg mb-6">
                Whether you're looking for a <strong>general contractor in Toronto</strong>, need 
                <strong>construction services in Ottawa</strong>, or require <strong>skilled trades in Mississauga</strong>, 
                Blue Knight Contracting has the local expertise and provincial reach to deliver exceptional results.
              </p>
              <p className="text-lg mb-6">
                Our <strong>Ontario construction company</strong> specializes in <strong>residential construction</strong>, 
                <strong>commercial building</strong>, and <strong>industrial projects</strong> across the province. 
                From <strong>Hamilton construction projects</strong> to <strong>London Ontario contractors</strong>, 
                we bring the same commitment to quality and professionalism to every job site.
              </p>
              <p className="text-lg">
                Contact Blue Knight Contracting today to discuss your <strong>Ontario construction project</strong> 
                and discover why we're the preferred choice for <strong>general contracting services</strong> 
                across <strong>Toronto</strong>, <strong>Ottawa</strong>, <strong>Mississauga</strong>, 
                <strong>Hamilton</strong>, and throughout <strong>Ontario</strong>.
              </p>
            </div>
            <div className="mt-8">
              <Button variant="cta" size="lg" className="text-lg px-8 py-4">
                <Phone className="mr-2 h-5 w-5" />
                Start Your Ontario Project Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Locations;